import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DialogForm,
  SplitTable,
  InstantSearch,
  DropdownButton,
  Button,
  HelpLabel,
  CustomerDetails,
  StoryblokHelpBlock,
  SplitTableRef,
} from "@components";
import { HiOutlineChevronDown, HiTrash, HiPencilAlt } from "react-icons/hi";

import { Customer } from "../../models";
import { CustomerConsolidatedInvoice } from "../CustomerDetails/CustomerConsolidatedInvoice";
import { useCache } from "../../context/CacheContext";
import { useSearchParams } from "react-router-dom";

interface CustomersListProps {
  customers: Customer[];
  isLoading: boolean;
  isOpen: (val: boolean) => void; // toggles the right pane
  createOrUpdate: (formData: Customer) => void;
  onCustomerSelect?: (customer: Customer | undefined) => void; // New callback
}

export const CustomersList: FC<CustomersListProps> = ({
  customers,
  isLoading,
  isOpen,
  createOrUpdate,
  onCustomerSelect,
}) => {
  const { t } = useTranslation(["common", "customer", "invoice"]);
  const navigate = useNavigate();
  const { updateCacheKey } = useCache();

  const [openedCustomer, setOpenedCustomer] = useState<Customer | undefined>();
  const [customerData, setCustomerData] = useState<Customer>(
    Customer.default(),
  );
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filteredCustomers, setFilteredCustomers] =
    useState<Customer[]>(customers);

  const splitTableRef = useRef<SplitTableRef>(null);

  // Update opened customer
  const updateOpenedCustomer = (cust: Customer | undefined) => {
    setOpenedCustomer(cust);

    if (cust) {
      isOpen(true);
      const customerName = Customer.getCustomerName(cust) || "unknown";
      const paramSafeName = customerName.toLowerCase().replace(/\s+/g, "-");
      navigate(`/customers?customer=${paramSafeName}`, { replace: true });
      splitTableRef.current?.setSplit(true);
    } else {
      isOpen(false);
      navigate(`/customers`, { replace: true });
      splitTableRef.current?.setSplit(false);
    }

    // Notify parent about customer selection
    if (onCustomerSelect) {
      onCustomerSelect(cust);
    }
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get the 'customer' query parameter
    const customerParam = searchParams.get("customer");

    if (customerParam && customers.length > 0) {
      // Find the matching customer by name
      const foundCustomer = customers.find(
        (cust) =>
          Customer.getCustomerName(cust)?.toLowerCase().replace(/\s+/g, "-") ===
          customerParam,
      );

      if (foundCustomer) {
        updateOpenedCustomer(foundCustomer); // Open the customer details
      }
    }
  }, [customers, searchParams]); // Runs when customers load or the URL changes

  // Filter customers based on search text
  useEffect(() => {
    if (searchText.trim().length > 0) {
      const lowerCaseSearchText = searchText.toLowerCase();
      setFilteredCustomers(
        customers.filter((cust) =>
          (Customer.getCustomerName(cust) || "")
            .toLowerCase()
            .includes(lowerCaseSearchText),
        ),
      );
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchText, customers]);

  // Table rows
  const tableRows = filteredCustomers.map((cust) => ({
    id: cust.id,
    isActive: cust.id === openedCustomer?.id,
    onRowClickData: cust,
    cells: [
      {
        id: "displayName",
        children:
          Customer.getCustomerName(cust) || t("customer:unknown_customer"),
        showOnSmallScreen: true,
      },
      ...(!openedCustomer
        ? [
            {
              id: "email",
              children: cust.contactEmail || "",
              showOnSmallScreen: false,
            },
          ]
        : []),
    ],
    actions: !openedCustomer
      ? [
          {
            id: "edit",
            icon: <HiPencilAlt />,
            onActionClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              createOrUpdate(cust);
            },
          },
          {
            id: "delete",
            icon: <HiTrash />,
            color: "failure",
            onActionClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              setCustomerData(cust);
              setDeleteModalOpen(true);
            },
          },
        ]
      : [],
  }));

  // Handle customer deletion
  const handleDelete = async () => {
    try {
      await Customer.remove(customerData);
      setDeleteModalOpen(false);
      updateCacheKey();

      if (openedCustomer?.id === customerData.id) {
        updateOpenedCustomer(undefined);
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Header */}
      <div className="flex flex-col justify-between items-start gap-4 mb-4">
        <div className={openedCustomer ? "hidden lg:block" : ""}>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-text sm:text-2xl">
              {t("customer:title")}
            </h1>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/customers"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </div>
          <p className="text-secondary text-sm sm:text-base pt-1 sm:pt-2 pb-2 sm:pb-4 dark:text-secondary">
            {t("customer:description")}
          </p>
        </div>

        {/* Filter bar - Hidden on mobile when customer details are open */}
        <div
          className={`flex flex-col md:flex-row gap-3 mb-4 w-full ${openedCustomer ? "hidden lg:flex" : "flex"}`}
        >
          {/* Customer search */}
          <div className="flex-grow relative">
            <InstantSearch
              placeholder={t("common:search_customer")}
              value={searchText}
              onChange={setSearchText}
            />
          </div>

          <div className="flex items-center">
            <Button
              variant="add"
              onClick={() => {
                createOrUpdate(Customer.default());
              }}
              size="md"
            >
              {t("customer:new_customer")}
            </Button>
          </div>
        </div>
      </div>

      {/* SplitTable */}
      <SplitTable
        ref={splitTableRef}
        hasActions
        isLoading={isLoading}
        tableRows={tableRows}
        tableHeaders={[
          {
            id: "displayName",
            children: t("common:customer"),
            showOnSmallScreen: true,
          },
          ...(!openedCustomer
            ? [
                {
                  id: "email",
                  children: t("common:email"),
                  showOnSmallScreen: false,
                },
              ]
            : []),
        ]}
        onRowClick={(cust: Customer | undefined) => updateOpenedCustomer(cust)}
        topRightContent={
          <div className="flex items-center">
            {openedCustomer && (
              <CustomerConsolidatedInvoice
                key={openedCustomer.workspaceId + openedCustomer.id}
                customerId={openedCustomer.id}
                renderTrigger={(invoiceAction) => (
                  <DropdownButton
                    placement="bottom-end"
                    buttonText={t("customer:actions.buttonText")}
                    buttonAppendIcon={<HiOutlineChevronDown className="ml-2" />}
                    items={[
                      ...(invoiceAction
                        ? [
                            {
                              text: (
                                <p
                                  className={`p-4 w-48 ${invoiceAction.disabled ? "text-secondary" : ""}`}
                                  aria-disabled={invoiceAction.disabled}
                                >
                                  {t("invoice:consolidated.title")}
                                </p>
                              ),
                              onClick: invoiceAction.onClick,
                            },
                          ]
                        : []),
                      {
                        text: (
                          <p className="p-4 w-48">
                            {t("customer:actions.edit")}
                          </p>
                        ),
                        onClick: () => {
                          createOrUpdate(openedCustomer);
                        },
                      },
                      {
                        text: (
                          <p className="p-4 w-48 text-lgb-red-300 dark:text-lgb-red-200 hover:text-lgb-red-200 hover:dark:text-lgb-red-300">
                            {t("customer:actions.delete")}
                          </p>
                        ),
                        onClick: () => {
                          setCustomerData(openedCustomer);
                          setDeleteModalOpen(true);
                        },
                      },
                    ]}
                  />
                )}
              />
            )}
          </div>
        }
        content={
          openedCustomer ? (
            <CustomerDetails
              customer={openedCustomer}
              edit={() => {
                createOrUpdate(openedCustomer);
              }}
              delete={() => {
                setCustomerData(openedCustomer);
                setDeleteModalOpen(true);
              }}
            />
          ) : (
            <div className="p-4 text-gray-600">
              {t("customer:split.no_customer_selected")}
            </div>
          )
        }
      />

      {/* Delete Confirmation Dialog */}
      <DialogForm
        title={`${t("customer:actions.delete_dialogTitle")} ${
          Customer.getCustomerName(customerData) ||
          t("customer:unknown_customer")
        }?`}
        dismissible
        show={isDeleteModalOpen}
        toggleModal={() => setDeleteModalOpen(false)}
        buttonConfirmColor="failure"
        confirmButton={handleDelete}
        buttonConfirmText={t("common:confirm")}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};
